import { FC, PropsWithChildren } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import {
  AgentMessageContextProvider,
  CopilotContextProvider,
} from '@dbt-labs/copilot-ui';
import { type Theme } from '@dbt-labs/sourdough';

import { PageContextProvider } from '@/components/Page';
import { FeatureFlagSet } from '@/featureFlags';
import { CopilotPaneContextProvider } from './CopilotPaneProvider';
import { DbtCloudAccountProvider, DbtCloudProject } from './DbtCloudProvider';
import DiscoveryUrqlProvider from './DiscoveryUrqlProvider';
import { FeatureFlagContextProvider } from './FeatureFlagProvider';
import { MissingDataHeaderProvider } from './MissingDataHeaderProvider';
import ModalProvider from './ModalProvider';
import { NavigationHistoryProvider } from './NavigationHistoryProvider';
import { NavPanelProvider } from './NavPanelProvider';
import { PanelProvider } from './PanelProvider';

interface ProvidersProps {
  reactQueryClient: QueryClient;
  dbtCloudAccountName: string | undefined;
  dbtCloudProjectName: string | undefined;
  defaultEnvironmentId: number | 'NO_DEFAULT_ENVIRONMENT' | undefined;
  discoveryUri?: string;
  dbtCloudProjects: DbtCloudProject[] | undefined;
  hasDeveloperLicense: boolean;
  isAdmin: boolean;
  isSinglePaneView: boolean;
  testFlagOverrides?: FeatureFlagSet;
  appContext: 'dbt-cloud' | 'snowflake-app' | 'cloud-ui';
  urlPrefix?: string;
  theme?: Theme;
}

export const GlobalProviders: FC<PropsWithChildren<ProvidersProps>> = ({
  reactQueryClient,
  dbtCloudAccountName,
  dbtCloudProjectName,
  dbtCloudProjects,
  defaultEnvironmentId,
  children,
  discoveryUri,
  hasDeveloperLicense,
  isAdmin,
  testFlagOverrides,
  appContext,
  urlPrefix,
  isSinglePaneView,
}) => {
  return (
    <FeatureFlagContextProvider testOverrides={testFlagOverrides}>
      <AgentMessageContextProvider>
        <CopilotPaneContextProvider>
          <PageContextProvider>
            <QueryClientProvider client={reactQueryClient}>
              <DiscoveryUrqlProvider uri={discoveryUri}>
                <DbtCloudAccountProvider
                  accountName={dbtCloudAccountName}
                  projectName={dbtCloudProjectName}
                  defaultEnvironmentId={defaultEnvironmentId}
                  dbtCloudProjects={dbtCloudProjects}
                  hasDeveloperLicense={hasDeveloperLicense}
                  isAdmin={isAdmin}
                  appContext={appContext}
                  urlPrefix={urlPrefix}
                >
                  <CopilotContextProvider>
                    <NavPanelProvider>
                      <MissingDataHeaderProvider>
                        <ModalProvider>
                          <PanelProvider isSinglePaneView={isSinglePaneView}>
                            <NavigationHistoryProvider>
                              <HelmetProvider>{children}</HelmetProvider>
                            </NavigationHistoryProvider>
                          </PanelProvider>
                        </ModalProvider>
                      </MissingDataHeaderProvider>
                    </NavPanelProvider>
                  </CopilotContextProvider>
                </DbtCloudAccountProvider>
              </DiscoveryUrqlProvider>
            </QueryClientProvider>
          </PageContextProvider>
        </CopilotPaneContextProvider>
      </AgentMessageContextProvider>
    </FeatureFlagContextProvider>
  );
};
